import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Divider, Button, CircularProgress, Fade, TextField, MenuItem
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { unwrap, listOf } from '../api';
import { isSuperAdmin, getTemplate, getTemplateName } from '../auth';

const PrivacyPolicy = () => {
  const { enqueueSnackbar } = useSnackbar();
  const superAdmin = isSuperAdmin();
  const myTemplate = getTemplate();

  // One privacy policy per template (site). Super admins pick one; admins only see their own.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(superAdmin ? '' : myTemplate);
  const [policyId, setPolicyId] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    if (superAdmin) {
      api.get('/api/templates', { params: { limit: 100 } }).then((res) => {
        const list = listOf(unwrap(res));
        setTemplates(list);
        if (list.length > 0) setSelectedTemplate(list[0]._id);
      }).catch(err => enqueueSnackbar(err.message, { variant: 'error' }));
    }
  }, [superAdmin]);

  useEffect(() => {
    if (selectedTemplate) fetchPolicy();
    else { setPolicyId(null); setContent(''); setUpdatedAt(null); }
  }, [selectedTemplate]);

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const params = superAdmin ? { template: selectedTemplate } : {};
      const [policy] = listOf(unwrap(await api.get('/api/privacy-policies', { params })));
      setPolicyId(policy?._id || null);
      setContent(policy?.content || '');
      setUpdatedAt(policy?.updatedAt || null);
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to load privacy policy', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // The backend has no upsert: update the existing policy, or create the first one.
      let body;
      if (policyId) {
        body = unwrap(await api.put(`/api/privacy-policies/${policyId}`, { content }));
      } else {
        const payload = { content };
        if (superAdmin) payload.template = selectedTemplate;
        body = unwrap(await api.post('/api/privacy-policies', payload));
      }
      setPolicyId(body.data?._id || policyId);
      setUpdatedAt(body.data?.updatedAt || null);
      enqueueSnackbar('Privacy policy saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectedTemplateName = superAdmin
    ? templates.find(t => t._id === selectedTemplate)?.name || ''
    : getTemplateName();

  return (
    <Box sx={{ mt: 2 }}>
      <Fade in timeout={600}>
        <Paper elevation={0} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h5">Privacy Policy</Typography>
              {selectedTemplateName && (
                <Typography variant="caption" color="text.secondary">{selectedTemplateName}</Typography>
              )}
              {updatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Last updated: {new Date(updatedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {superAdmin && templates.length > 0 && (
                <TextField
                  select size="small" label="Site template"
                  value={selectedTemplate}
                  onChange={e => setSelectedTemplate(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  {templates.map(t => (
                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                  ))}
                </TextField>
              )}
              <Button variant="contained" onClick={handleSave}
                disabled={saving || loading || !selectedTemplate} sx={{ fontWeight: 600 }}>
                {saving ? <CircularProgress size={22} /> : 'Save'}
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            {!selectedTemplate ? (
              <Typography color="text.secondary">Select a site template to edit its privacy policy.</Typography>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TextField
                value={content}
                onChange={e => setContent(e.target.value)}
                multiline
                minRows={20}
                fullWidth
                placeholder="Enter your privacy policy content here..."
                variant="outlined"
              />
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default PrivacyPolicy;
